import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MessageSquare, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);

  const [previewToken, setPreviewToken] = useState(null);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      
      // Handle preview mode - show the token directly
      if (response.data.mode === 'preview' && response.data.reset_token) {
        setPreviewToken(response.data.reset_token);
        toast.success('Preview mode: Reset link generated below');
      } else {
        toast.success('If that email exists, a reset link has been sent');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setPasswordReset(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // If we have a token, show the reset form
  if (token) {
    if (passwordReset) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-700 dark:text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Password Reset!</h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">Your password has been successfully reset.</p>
            <Link to="/login">
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="back-to-login">
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
          
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" data-testid="reset-title">
            Set new password
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">
            Enter your new password below
          </p>
          
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
                className="h-12"
              />
              <p className="text-xs text-slate-500">Minimum 6 characters</p>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-emerald-700 hover:bg-emerald-800" 
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Show request reset form
  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950 flex items-center justify-center p-8">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-emerald-700 dark:text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {previewToken ? 'Reset Link Generated' : 'Check your email'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            {previewToken 
              ? 'Preview mode: Click the link below to reset your password.'
              : "If an account exists with that email, we've sent a password reset link."
            }
          </p>
          
          {/* Preview mode: Show clickable reset link */}
          {previewToken && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-left">
              <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-2">
                🔧 Preview Mode - No email sent
              </p>
              <Link 
                to={`/reset-password?token=${previewToken}`}
                className="text-emerald-700 dark:text-emerald-400 hover:underline font-mono text-sm break-all"
                data-testid="preview-reset-link"
              >
                Click here to reset password →
              </Link>
            </div>
          )}
          
          <Link to="/login">
            <Button variant="outline" data-testid="back-to-login-link">Back to Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>
        
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-2xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
        </div>
        
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" data-testid="forgot-title">
          Forgot password?
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-8">
          No worries, we'll send you reset instructions
        </p>
        
        <form onSubmit={handleRequestReset} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="forgot-email"
              className="h-12"
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12 bg-emerald-700 hover:bg-emerald-800" 
            disabled={loading}
            data-testid="forgot-submit"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
